import React, { useState, useEffect } from 'react';
import { Send, CheckCircle, AlertCircle, ArrowRight, Shield, Mail, UserCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuths } from '../context/AuthProviders';
import { getUserProfile } from '../services/Queries/Auths';


const ApplyForHR = () => {
  const { user } = useAuths();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
const { logout } = useAuths()
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        setProfileLoading(false);
        return;
      }
      
      try {
        const profile = await getUserProfile(user.id);
        setProfileData(profile);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [user?.id]);

  const handleApply = async () => {
    if (!user) {
      toast.error('Please log in first');
      return;
    }

    setLoading(true);

    try {
      // Generate the admin review link with user ID
      const baseUrl = window.location.origin;
      const reviewLink = `${baseUrl}/admin/promote-to-hr/${user.id}`;

      // Create email content for admin
      const emailContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
          <div style="background: linear-gradient(135deg, #4F46E5, #7C3AED); padding: 30px; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 24px;">🎯 HR Role Application</h1>
          </div>
          
          <div style="padding: 30px;">
            <p style="font-size: 16px; color: #333;">A user has applied for HR role:</p>
            
            <div style="background: #F3F4F6; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <p style="margin: 5px 0;"><strong>User ID:</strong> ${user.id}</p>
              <p style="margin: 5px 0;"><strong>Email:</strong> ${user.email}</p>
              <p style="margin: 5px 0;"><strong>Name:</strong> ${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}</p>
              <p style="margin: 5px 0;"><strong>Current Role:</strong> ${profileData?.user_type || 'user'}</p>
              <p style="margin: 5px 0;"><strong>Applied At:</strong> ${new Date().toLocaleString()}</p>
            </div>

            <div style="text-align: center; margin: 30px 0;">
              <a href="${reviewLink}" 
                 style="background: linear-gradient(135deg, #4F46E5, #7C3AED); color: white; padding: 14px 28px; 
                        text-decoration: none; border-radius: 50px; font-weight: 600; display: inline-block;
                        box-shadow: 0 4px 6px rgba(79, 70, 229, 0.3);">
                👑 Review & Promote to HR
              </a>
            </div>

            <p style="color: #666; font-size: 14px; text-align: center;">
              Or copy this link: <br>
              <code style="background: #F3F4F6; padding: 8px; display: block; margin-top: 10px; word-break: break-all;">
                ${reviewLink}
              </code>
            </p>
          </div>

          <div style="background: #F9FAFB; padding: 20px; text-align: center; border-top: 1px solid #E5E7EB;">
            <p style="color: #9CA3AF; font-size: 12px;">
              Powered by <strong style="color: #4F46E5;">Trio Developers</strong> • Secure HR Management
            </p>
          </div>
        </div>
      `;

      // Send email to admin
      const formPayload = new FormData();
      formPayload.append('email', 'abidmani790@gmail.com');
      formPayload.append('subject', `HR Application - ${user.email}`);
      formPayload.append('html', emailContent);
      formPayload.append('type', 'hr_application');

      const response = await axios.post(
        'https://samahomecare.com/promoteHr.php',
        formPayload,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.success) {
        setSubmitted(true);
        toast.success('Application sent! Admin will review it.');
      } else {
        throw new Error('Failed to send application');
      }

    } catch (error) {
      console.error('Error applying for HR:', error);
      toast.error('Failed to send application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Not Logged In</h2>
          <p className="text-gray-600 mb-6">Please log in to apply for HR role</p>
          <a
            href="/login"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Check if user is already HR or Admin using user_type field
  const isAlreadyHR = profileData?.user_type === 'hr' || profileData?.user_type === 'admin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Black Hero Section */}
      <div className="bg-black text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 mt-5">
            {isAlreadyHR ? ' HR Member Access' : ' Elevate Your Career'}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {isAlreadyHR 
              ? 'You already have HR privileges. Explore the HR dashboard to manage applications.'
              : 'Join the HR team and help shape the future of our organization. Take the next step in your professional journey.'}
          </p>
          
          {/* Role Badge */}
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-gray-800 rounded-full">
            <UserCheck className="w-5 h-5 text-blue-400" />
            <span className="text-sm font-medium">
              Current Role: <span className="text-blue-400 capitalize">{profileData?.user_type || 'user'}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className={`p-8 text-center ${
            isAlreadyHR 
              ? 'bg-gradient-to-r from-green-600 to-emerald-600' 
              : 'bg-gradient-to-r from-blue-600 to-indigo-600'
          }`}>
            {isAlreadyHR ? (
              <CheckCircle className="w-20 h-20 text-white mx-auto mb-4" />
            ) : (
              <Shield className="w-20 h-20 text-white mx-auto mb-4" />
            )}
            <h1 className="text-3xl font-bold text-white mb-2">
              {isAlreadyHR ? 'Already an HR Member' : 'Become an HR Member'}
            </h1>
            <p className="text-white/90 text-lg">
              {isAlreadyHR 
                ? 'You have full access to HR features' 
                : 'Take the next step in your career'}
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {isAlreadyHR ? (
              // Already HR/Admin View
              <div className="text-center py-8">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <UserCheck className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">You're Already an HR Member!</h2>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  You already have HR privileges. You can access the HR dashboard to manage applications, review forms, and perform HR tasks.
                </p>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
                  <p className="text-sm text-blue-800">
                    <strong>Your Current Role:</strong>
                  </p>
                  <p className="text-lg font-semibold text-blue-700 capitalize mt-1">
                    {profileData?.user_type === 'admin' ? 'Administrator' : 'HR Manager'}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/hr/dashboard"
                    className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Go to HR Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                  <a
                    href="/"
                    className="inline-flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Back to Home
                  </a>
                </div>
              </div>
            ) : submitted ? (
              // Success State
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">Application Sent!</h2>
                <p className="text-gray-600 mb-6">
                  Your HR application has been submitted successfully. The admin will review it and you'll receive an email once your role is updated.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>Next Steps:</strong>
                  </p>
                  <ul className="text-sm text-blue-700 mt-2 space-y-2">
                    <li>• Admin will review your application</li>
                    <li>• You'll receive a confirmation email</li>
                    <li>• Once approved, you'll get HR access</li>
                  </ul>
                </div>
                <a
                  href="/"
                  className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Go to Dashboard
                </a>
              </div>
            ) : (
              // Application Form
              <>
                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
                  <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">
                    <Mail className="w-5 h-5 mr-2" />
                    How to Apply for HR Role
                  </h3>
                  <ul className="space-y-3 text-blue-700">
                    <li className="flex items-start">
                      <span className="inline-block w-5 h-5 bg-blue-200 rounded-full text-blue-800 text-sm font-medium flex items-center justify-center mr-3 mt-0.5">1</span>
                      <span>Click the "Apply for HR Role" button below</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-5 h-5 bg-blue-200 rounded-full text-blue-800 text-sm font-medium flex items-center justify-center mr-3 mt-0.5">2</span>
                      <span>An email will be sent to the admin with your application</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-5 h-5 bg-blue-200 rounded-full text-blue-800 text-sm font-medium flex items-center justify-center mr-3 mt-0.5">3</span>
                      <span>Admin will review your request and update your role</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-5 h-5 bg-blue-200 rounded-full text-blue-800 text-sm font-medium flex items-center justify-center mr-3 mt-0.5">4</span>
                      <span>You'll get HR access once approved</span>
                    </li>
                     <li className="flex items-start">
                      <span className="inline-block w-5 h-5 bg-blue-200 rounded-full text-blue-800 text-sm font-medium flex items-center justify-center mr-3 mt-0.5">5</span>
                      <span>Press logout to fully become the hr</span>
                    </li>
                  </ul>
                </div>

                {/* User Info Summary */}
                <div className="bg-gray-50 rounded-xl p-6 mb-8">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Your Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Email</span>
                      <span className="font-medium text-gray-900">{user?.email}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Name</span>
                      <span className="font-medium text-gray-900">
                        {user?.user_metadata?.first_name || ''} {user?.user_metadata?.last_name || ''}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Current Role</span>
                      <span className="px-3 py-1 bg-gray-200 rounded-full text-xs font-medium">
                        {profileData?.user_type || 'user'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600">Requesting Role</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                        HR Member
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="text-center">
                  <button
                    onClick={handleApply}
                    disabled={loading}
                    className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                        Sending Application...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Apply for HR Role
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </button>
                  <p className="text-sm text-gray-500 mt-4">
                    This will notify the admin who will review your application
                  </p>
                </div>
                <button
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                onClick={()=>{
                  logout();
                }}
                >
                  Logout
                </button>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Powered by{' '}
              <a 
                href="https://triodevelopers.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Trio Developers
              </a>
              {' '}• Secure HR Management System
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyForHR;